import React, { useState } from 'react';
import { ASSET_URL } from '../../lib/service-registry';

/**
 * Cross-service ticket submit modal (MYB-357)
 * Calls asset.tscorp.ai/api/platform/tickets with Platform Token cookie
 */

const TICKET_API = `${ASSET_URL}/api/platform/tickets`;

const CATEGORIES = [
  { value: 'bug', label: '버그' },
  { value: 'improvement', label: '개선사항' },
];
const SERVICES = [
  { value: 'asset', label: '자산 관리' },
  { value: 'hr', label: '인사 관리' },
  { value: 'landing', label: '서비스 허브' },
  { value: 'other', label: '기타' },
];

interface Props {
  defaultService?: string;
  onClose: () => void;
  onSuccess?: () => void;
}

const TicketSubmitModal: React.FC<Props> = ({ defaultService = 'hr', onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('bug');
  const [service, setService] = useState(defaultService);
  const [description, setDescription] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState<{ linearId: string } | null>(null);

  const handleSubmit = async () => {
    if (!title.trim()) { setError('제목을 입력해 주세요'); return; }
    setSending(true); setError('');
    try {
      const res = await fetch(TICKET_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ title: title.trim(), category, service, description: description.trim() || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setSuccess({ linearId: data.ticket.linearId });
        onSuccess?.();
      } else {
        setError(data.error || '등록에 실패했습니다');
      }
    } catch { setError('네트워크 오류가 발생했습니다'); }
    finally { setSending(false); }
  };

  if (success) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(32,33,36,0.5)' }} onClick={onClose}>
        <div style={{ background: '#fff', borderRadius: '1rem', width: '100%', maxWidth: 400, margin: '0 16px', padding: 32, textAlign: 'center' }} onClick={e => e.stopPropagation()}>
          <div style={{ width: 48, height: 48, margin: '0 auto 16px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e6f4ea' }}>
            <svg style={{ width: 24, height: 24, color: '#1e8e3e' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <p style={{ fontSize: '1rem', fontWeight: 500, color: '#202124' }}>이슈가 접수되었습니다</p>
          <p style={{ fontSize: '0.875rem', color: '#5f6368', marginTop: 8 }}>{success.linearId}로 등록되었습니다.</p>
          <button onClick={onClose} style={{ marginTop: 24, padding: '8px 20px', border: '1px solid #e8eaed', borderRadius: '0.5rem', background: '#fff', color: '#5f6368', cursor: 'pointer', fontSize: '0.875rem' }}>닫기</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(32,33,36,0.5)' }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: '1rem', width: '100%', maxWidth: 480, margin: '0 16px' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e8eaed' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 500, color: '#202124', margin: 0 }}>이슈 등록</h3>
          <button aria-label="닫기" onClick={onClose} style={{ width: 32, height: 32, border: 'none', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', color: '#80868b', cursor: 'pointer' }}>
            <svg style={{ width: 20, height: 20 }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#5f6368', marginBottom: 4 }}>제목 *</label>
            <input value={title} onChange={e => setTitle(e.target.value)} maxLength={100} placeholder="문제 또는 개선사항을 한 문장으로 입력하세요"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e8eaed', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#5f6368', marginBottom: 8 }}>카테고리 *</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {CATEGORIES.map(c => (
                <button key={c.value} onClick={() => setCategory(c.value)}
                  style={{ padding: '6px 16px', borderRadius: '1rem', fontSize: '0.875rem', fontWeight: 500, cursor: 'pointer', border: `1.5px solid ${category === c.value ? '#1a73e8' : '#e8eaed'}`, background: category === c.value ? '#e8f0fe' : '#fff', color: category === c.value ? '#1a73e8' : '#5f6368' }}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#5f6368', marginBottom: 4 }}>서비스 *</label>
            <select aria-label="서비스 선택" value={service} onChange={e => setService(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e8eaed', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', boxSizing: 'border-box' }}>
              {SERVICES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: '#5f6368', marginBottom: 4 }}>설명</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} maxLength={2000} placeholder="구체적인 내용, 재현 방법, 기대 동작을 입력하세요 (선택사항)"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e8eaed', borderRadius: '0.5rem', fontSize: '0.875rem', outline: 'none', resize: 'vertical', boxSizing: 'border-box' }} />
            <div style={{ textAlign: 'right', fontSize: '0.75rem', color: '#80868b', marginTop: 4 }}>{description.length}/2000</div>
          </div>
          {error && <div style={{ padding: '8px 12px', borderRadius: '0.5rem', fontSize: '0.875rem', background: '#fce8e6', color: '#d93025', border: '1px solid rgba(217,48,37,0.2)' }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 20px', borderTop: '1px solid #e8eaed' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: '0.875rem', borderRadius: '0.5rem', border: '1px solid #e8eaed', background: '#fff', color: '#5f6368', cursor: 'pointer' }}>취소</button>
          <button onClick={handleSubmit} disabled={sending} style={{ padding: '8px 16px', fontSize: '0.875rem', borderRadius: '0.5rem', border: 'none', background: '#1a73e8', color: '#fff', cursor: 'pointer', opacity: sending ? 0.6 : 1 }}>
            {sending ? '등록 중...' : '이슈 등록'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TicketSubmitModal;
